import { useEffect, useState } from "react";
import { useAppState } from "../contexts/AppStateContext";
import type { AnalyticsData } from "../services/analyticsService";
import { getAnalytics } from "../services/analyticsService";
import { fetchAllCampuses } from "../services/childDataService";
import { getAllOrders } from "../services/orderService";
import { getAllPackages } from "../services/packageService";
import type { Order, OrderStatus } from "../types/api";
import { ACTIVE_STATUSES, PAGE_SIZE } from "../utils/dashboardHelpers";

interface UseDashboardDataReturn {
  // Campuses
  campusesLoading: boolean;
  campusesError: string;
  
  // Analytics
  analytics: AnalyticsData | null;
  analyticsLoading: boolean;
  analyticsError: string;
  
  // Orders
  orders: Order[];
  ordersLoading: boolean;
  ordersError: string;
  page: number;
  totalPages: number;
  statusFilter: OrderStatus[];
  setPage: (page: number) => void;
  setStatusFilter: (filter: OrderStatus[]) => void;
}

export const useDashboardData = (): UseDashboardDataReturn => {
  const { dispatch } = useAppState();
  
  // Campuses state
  const [campusesLoading, setCampusesLoading] = useState<boolean>(true);
  const [campusesError, setCampusesError] = useState<string>("");
  
  // Analytics state
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState<boolean>(false);
  const [analyticsError, setAnalyticsError] = useState<string>("");
  
  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [ordersError, setOrdersError] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>(ACTIVE_STATUSES);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Initialize: Load campuses with children
  useEffect(() => {
    const loadCampusesWithChildren = async () => {
      try {
        setCampusesLoading(true);
        setCampusesError("");
        const campusesWithChildren = await fetchAllCampuses();
        dispatch({ type: "SET_CAMPUSES", payload: campusesWithChildren });
      } catch (error) {
        console.error("Error loading campuses:", error);
        setCampusesError("Failed to load campuses. Please try again.");
      } finally {
        setCampusesLoading(false);
      }
    };

    loadCampusesWithChildren();
  }, [dispatch]);

  // Initialize: Fetch analytics data
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setAnalyticsLoading(true);
        setAnalyticsError("");
        const analyticsData = await getAnalytics();
        setAnalytics(analyticsData);
      } catch (error) {
        console.error("Error loading analytics:", error);
        setAnalyticsError("Failed to load analytics. Please try again.");
      } finally {
        setAnalyticsLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  // Fetch orders and packages data (depends on page and statusFilter)
  useEffect(() => {
    const loadOrdersAndPackages = async () => {
      try {
        setOrdersLoading(true);
        setOrdersError("");

        // Fetch packages and orders in parallel
        const [packagesData, ordersResponse] = await Promise.all([
          getAllPackages(),
          getAllOrders(
            page,
            PAGE_SIZE,
            statusFilter.length > 0 ? statusFilter : undefined
          ),
        ]);

        dispatch({ type: "SET_PACKAGES", payload: packagesData });
        setOrders(ordersResponse.orders);
        setTotalPages(ordersResponse.pagination.totalPages);
      } catch (error) {
        console.error("Error loading orders and packages:", error);
        setOrdersError("Failed to load orders. Please try again.");
      } finally {
        setOrdersLoading(false);
      }
    };

    loadOrdersAndPackages();
  }, [page, statusFilter, dispatch]);

  return {
    // Campuses
    campusesLoading,
    campusesError,
    
    // Analytics
    analytics,
    analyticsLoading,
    analyticsError,
    
    // Orders
    orders,
    ordersLoading,
    ordersError,
    page,
    totalPages,
    statusFilter,
    setPage,
    setStatusFilter,
  };
};
